import React, { forwardRef, useImperativeHandle, useEffect, useRef, useMemo, useState, useCallback } from 'react'
import { Form, FormInstance, message as $message } from 'antd'
import debounce from 'lodash/debounce'

import { jstLogSearch } from '@/utils/logger'

import type { QueryFilterType } from './typing'
import type { ContextValueType } from './ConfigProvider/typing'
import FieldItemList from './components/FieldItemList'
import FieldItemGroup from './components/FieldItemGroup'
import QuickSearchPreOptions from './components/QuickSearchPreOptions'
import QuickSearchList from './components/QuickSearchList'
import AddQuickSearch from './components/AddQuickSearch'
import SortQuickSearch from './components/SortQuickSearch'
import UpgradeQuickSearchConfig from './components/UpgradeQuickSearchConfig'
import ConfigProvider from './ConfigProvider/index'
import { useMemoizedFn } from 'ahooks'
import { useQueryLayout } from './hooks/useQueryLayout'
import styles from './index.less'
import middleStyles from './styles.less'
import classnames from 'classnames'
import SearchFieldsConfigBtn from '@/components/QueryFilterWithConfig/components/ConfigDrawer/trigger.jsx'
import useRecordOperation from '@/hooks/useRecordOperation'
import moment from 'moment'
import { getUnshowFieldNames, transConfigToRenderArr } from '@/components/QueryFilterWithConfig/helper'
import { newUserMoment } from '@/components/QueryFilterWithConfig/index.jsx'
import { useGenConfigFields } from '@/components/QueryFilterWithConfig/components/ConfigDrawer/hooks'
import SearchArea, { showCollapseBtnRowIndex } from './components/SearchArea'
import CustomFieldsList from './components/CustomFieldsList'

import useQuickSearch from './hooks/useQuickSearch'
import useFormLocalData from './hooks/useFormLocalData'

import checkShouldClearQuickSearchSelected from './helper/checkShouldClearQuickSearchSelected'
import checkShouldImmediatelyTriggerSearch, {
    getTriggerOnChangeFields,
    getFlatFieldsMap
} from './helper/checkShouldImmediatelyTriggerSearch'

export { QuickSearchGroups } from './components/QuickSearchGroups'
export { Layout, VIEW_TYPE } from './hooks/useQueryLayout'
export type { ContextValueType, QueryFilterType }

const debounceTimer = 300

enum QuickSearchPlacement {
    top = 'top',
    bottom = 'bottom'
}

/**
 * 新版搜索区域
 * @param {QueryFilterType} QueryFilterType 入参对象
 * @returns {React.Node} 返回内容
 */
export default forwardRef<FormInstance, QueryFilterType>((props, ref) => {
    const {
        className,
        contentClassName,
        name,
        form: outForm,
        customFields,
        fields = [],
        groupFields,
        onValuesChange = () => {},
        onChange,
        initialValues = {},
        extraInitialValues = {},
        defaultIsShowCollapsedBtn,
        renderExtraOperate,
        renderCustomBottomArea,
        reset,
        renderHeader,
        renderHeaderAfterLine,
        defaultCloNum,
        hideRest,
        hideSearchArea,
        queryConfigKey = '',
        userCreated,
        queryConfigRedDotKey, // 常用筛选项设置 按钮上的小红点，不传则使用queryConfigKey
        uid,
        defaultSortText,
        whenHideDisplayNone,
        addSearchBtnStyle = {},
        hideShowQuickSort = false,
        showFieldLabel = false,
        sortFields,
        sortValues,
        quickSearchEnable,
        quickSearchSortable,
        quickSearchPlacement,
        quickSearchConfigKey,
        quickSearchOldConfigKey,
        quickSearchEmptyMessage = '请先设置筛选条件和排序规则',
        quickSearchSelected: inputQuickSearchSelected,
        quickSearchPreOptions,
        quickSearchPreSelectDisplay,
        quickSearchPreOptionsRender,
        setQuickSearchSelected: btnSetQuickSearchSelected,
        configSaveOnCompamyLevel,
        onApplyQuickSearch,
        onRefresh
    } = props
    const tableNameAndId = `${name}_queryFilter`

    const [form] = Form.useForm(outForm)

    useFormLocalData({ form })

    const widthRef = useRef<HTMLDivElement>(null)
    //   const [configFields, setConfigFields] = useState(fields)
    const [initConfig, setInitConfig] = useState({})
    const isNewUser = useMemo(() => {
        return userCreated && moment(userCreated).isAfter(newUserMoment)
    }, [userCreated])
    const configFields = transConfigToRenderArr(fields, initConfig, whenHideDisplayNone, queryConfigKey)

    const { isRecord: queryConfigRedDotIsRecord, saveRecordStatus: saveQueryConfigRedDotRecordStatus } =
        useRecordOperation({
            name: queryConfigRedDotKey || queryConfigKey,
            latitudeType: uid
        })

    const handelSearch = useMemoizedFn(async () => {
        let values = null

        try {
            values = await form.validateFields()
        } catch (e) {
            console.error('catchError', e)
            const { errorFields } = e
            const firstName = errorFields?.[0]?.name
            scrollTo(firstName)
            $message.error('请填写正确搜索内容')
            return Promise.reject(new Error('操作失败'))
        }

        jstLogSearch({
            args: values
        })

        onChange(values as any)
    })

    // 立即触发搜索
    const immediatelySearch = ({ changedValues, noClear }) => {
        const values = form.getFieldsValue()
        const triggerArr = getTriggerOnChangeFields({ fields, customFields })
        const fieldsMap = getFlatFieldsMap({ fields, customFields })

        const getCleanValues = () => {
            for (let fieldKey of Object.keys(changedValues)) {
                const clearValueFieldsWhenOnChange = fieldsMap?.[fieldKey]?.clearValueFieldsWhenOnChange

                // 布尔值且为true，清除除了自己的所有字段
                if (typeof clearValueFieldsWhenOnChange === 'boolean' && clearValueFieldsWhenOnChange) {
                    return Object.keys(values).reduce((t: any, c: string) => {
                        // 自己
                        if (c === fieldKey) {
                            t[c] = values[c]
                        } else {
                            // 其他
                            t[c] = undefined
                        }

                        return t
                    }, {})
                }

                // 数组，只清除指定的值
                if (Array.isArray(clearValueFieldsWhenOnChange)) {
                    return Object.keys(values).reduce((t: any, c: string) => {
                        // 需要清除的
                        if (clearValueFieldsWhenOnChange.includes(c)) {
                            t[c] = undefined
                        } else {
                            t[c] = values[c]
                        }

                        return t
                    }, {})
                }
            }

            // 保留其他直接触发变更的字段值
            return Object.keys(values).reduce((t: any, c: string) => {
                // 默认逻辑为只保留主动触发搜索项的值（分销客户中心新增）
                // 分销客户中心，又要求不清空其他搜索项的值 - 20240325
                if (noClear) {
                    t[c] = values[c]
                    return t
                }
                if (triggerArr.includes(c)) {
                    t[c] = values[c]
                } else {
                    t[c] = undefined
                }

                return t
            }, {})
        }

        const cleanValues = getCleanValues()

        jstLogSearch({
            args: cleanValues
        })

        onChange(cleanValues as any)
    }

    const setQuickSearch = (values: any) => {
        form.resetFields()
        form.setFieldsValue(values)
        onChange(values as any)
    }

    useEffect(() => {
        if (Object.keys(extraInitialValues).length) {
            form.setFieldsValue({ ...initialValues, ...extraInitialValues })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraInitialValues])

    const handleReset = useMemoizedFn(() => {
        form.resetFields()
        onChange(initialValues as any)
        btnSetQuickSearchSelected?.('')
    })

    useImperativeHandle(
        ref,
        () => ({
            ...form,
            search: handelSearch,
            handleReset,
            setQuickSearch
        }),
        [form]
    )

    // 回车搜索
    useEffect(() => {
        setTimeout(() => {
            const $el = document.getElementById(tableNameAndId)

            if (!$el) {
                return
            }

            $el.onkeyup = debounce(event => {
                const noEnterEvent = !event?.target?.dataset?.keyenterevent
                if (noEnterEvent && (event.key === 'Enter' || event.code === 'Enter')) {
                    handelSearch()
                }
            }, debounceTimer)
        })
    }, [name, handelSearch, tableNameAndId])

    const layoutConfig = { hasButton: true, widthRef, defaultCloNum, tableNameAndId, quickSearchEnable }

    const {
        fields: fieldsList,
        fieldsGroup,
        colNumPerLine,
        collapsed,
        toggleCollapsed,
        maxRowIndex,
        rowIndex,
        groupLayout
    } = useQueryLayout(configFields, layoutConfig)

    const {
        quickSearchOptions,
        quickSearchSelected,
        setQuickSearchSelected,
        addQuickSearchConfig,
        sortQuickSearchConfig,
        deleteQuickSearchConfig,
        quickSearchInited,
        needUpgradeQuickSearchConfig,
        upgradeQuickSearchConfig,
        quickSearchUpgrading
    } = useQuickSearch({
        name,
        form,
        fieldsList,
        fieldsGroup,
        sortFields,
        sortValues,
        defaultSortText,
        quickSearchEnable,
        quickSearchEmptyMessage,
        quickSearchConfigKey,
        quickSearchOldConfigKey,
        selected: inputQuickSearchSelected,
        onApplyQuickSearch,
        configSaveOnCompamyLevel
    })

    let memoData = useGenConfigFields(fields, null, true)
    // 当数据请求回来时，对页面进行初始化
    const initConfigFields = useCallback(
        initConfig => {
            if (Object.prototype.toString.call(initConfig) === '[object Object]' && Object.keys(initConfig).length) {
                setInitConfig(initConfig)
            }
            if (initConfig === null) {
                // 用户从未配置过该页面查询条件
                if (memoData.config) {
                    setInitConfig(memoData.config)
                }
            }
        },
        [memoData.config]
    )

    const handleValuesChange = (...args) => {
        const changedValues = args[0]
        const allValues = form.getFieldsValue()

        // 是否触发搜索， 触发搜索后其他项不要清空
        const [shouldImmediatelyTriggerSearch, shouldImmediatelyTriggerSearchNoClear] =
            checkShouldImmediatelyTriggerSearch({
                fields,
                customFields,
                changedValues
            })
        // 是否清除快捷查询选中项
        const shouldClearQuickSearchSelected = checkShouldClearQuickSearchSelected({
            quickSearchPreOptions,
            quickSearchPreSelectDisplay,
            quickSearchOptions,
            quickSearchSelected,
            changedValues,
            allValues
        })

        // 输入自动清除快捷查询选中
        if (shouldClearQuickSearchSelected) {
            btnSetQuickSearchSelected?.('')
        }

        // 输入自动触发搜索
        if (shouldImmediatelyTriggerSearch) {
            immediatelySearch({ changedValues, noClear: shouldImmediatelyTriggerSearchNoClear })
        }

        onValuesChange?.(...args)
    }

    const onCollapse = () => {
        if (collapsed && maxRowIndex <= showCollapseBtnRowIndex) {
            toggleCollapsed()
        }
    }

    const contentAfterLastFieldItem = queryConfigKey ? (
        <div
            style={{
                height: 26,
                display: 'flex',
                marginBottom: 8,
                alignItems: 'center',
                position: 'relative',
                ...addSearchBtnStyle
            }}
        >
            <SearchFieldsConfigBtn
                simple
                // registerMethods={registerMethods}
                // unRegisterMethods={unRegisterMethods}
                queryConfigKey={queryConfigKey}
                fields={fields}
                redDot={!queryConfigRedDotIsRecord}
                onClick={() => saveQueryConfigRedDotRecordStatus(true)}
                btnProps={{
                    style: { alignSelf: 'center', marginRight: '8px' }
                }}
                groupFields={groupFields}
                isNewUser={isNewUser}
                onCollapse={onCollapse}
                initCallback={config => {
                    initConfigFields(config)
                }}
                onSaveCallback={data => {
                    initConfigFields(data)
                    const unshowFieldNames = getUnshowFieldNames(data)
                    form.resetFields(unshowFieldNames)
                    onCollapse()
                }}
                hideShowQuickSort={hideShowQuickSort}
            >
                添加/隐藏搜索条件
            </SearchFieldsConfigBtn>
        </div>
    ) : null

    const contentBeforeCollapsedBtn = null

    const contextValue: ContextValueType = {
        form,
        handelSearch,
        handleReset,
        reset,
        initialValues,
        renderExtraOperate,
        colNumPerLine,
        hideRest,
        hideSearchArea,
        collapsed,
        defaultIsShowCollapsedBtn: groupLayout ? false : defaultIsShowCollapsedBtn,
        toggleCollapsed,
        rowIndex,
        maxRowIndex,
        contentAfterLastFieldItem,
        contentBeforeCollapsedBtn,
        sortFields,
        quickSearchPreOptions,
        quickSearchPreSelectDisplay,
        quickSearchOptions,
        quickSearchSelected,
        quickSearchPreOptionsRender,
        setQuickSearchSelected,
        addQuickSearchConfig,
        deleteQuickSearchConfig,
        onRefresh,
        quickSearchEnable,
        quickSearchPlacement,
        groupLayout
    }

    const headerAfterLineNode = useMemo(() => {
        const node = renderHeaderAfterLine?.()
        if (node) {
            return <div className={styles.headerAfterLine}>{node}</div>
        }
        return null
    }, [renderHeaderAfterLine])

    const headerNode = useMemo(() => {
        const node = renderHeader?.()
        if (node) {
            return <div className={classnames(styles.topNoCollapsedStyles, styles.border)}>{node}</div>
        }
        return null
    }, [renderHeader])

    const renderTopQuickSearchNode = () => {
        const list = [
            {
                key: 'preOptions',
                renderComponent() {
                    return (
                        <QuickSearchPreOptions
                            fieldsGroup={fieldsGroup}
                            sortFields={sortFields}
                            quickSearchPreOptions={quickSearchPreOptions}
                            quickSearchPreSelectDisplay={quickSearchPreSelectDisplay}
                            quickSearchOptions={quickSearchOptions}
                            quickSearchSelected={quickSearchSelected}
                            quickSearchPreOptionsRender={quickSearchPreOptionsRender}
                            onSelectQuickSearch={setQuickSearchSelected}
                            onDeleteQuickSearch={deleteQuickSearchConfig}
                        />
                    )
                },
                visible: quickSearchPreOptions?.length
            },
            {
                key: 'options',
                renderComponent() {
                    return (
                        <QuickSearchList
                            fieldsGroup={fieldsGroup}
                            sortFields={sortFields}
                            quickSearchPreOptions={quickSearchPreOptions}
                            quickSearchOptions={quickSearchOptions}
                            quickSearchSelected={quickSearchSelected}
                            onSelectQuickSearch={setQuickSearchSelected}
                            onDeleteQuickSearch={deleteQuickSearchConfig}
                        >
                            <AddQuickSearch onClick={addQuickSearchConfig} />
                            {quickSearchOptions?.length ? <SortQuickSearch onClick={sortQuickSearchConfig} /> : null}
                        </QuickSearchList>
                    )
                },
                visible:
                    quickSearchEnable && (!quickSearchPlacement || quickSearchPlacement === QuickSearchPlacement.top)
            }
        ].filter(v => v.visible)

        if (!list.length) {
            return null
        }

        return list.map(item => <React.Fragment key={item.key}>{item.renderComponent()}</React.Fragment>)
    }

    const renderBottomQuickSearchNode = () => {
        if (renderCustomBottomArea) {
            return renderCustomBottomArea()
        }

        if (!quickSearchEnable) {
            return null
        }

        const list = [
            {
                key: 'options',
                renderComponent() {
                    const renderLeft = () => {
                        if (!quickSearchInited) {
                            return null
                        }

                        if (needUpgradeQuickSearchConfig) {
                            return (
                                <UpgradeQuickSearchConfig
                                    loading={quickSearchUpgrading}
                                    onClick={upgradeQuickSearchConfig}
                                />
                            )
                        }

                        return (
                            <QuickSearchList
                                fieldsList={fieldsList}
                                fieldsGroup={fieldsGroup}
                                sortFields={sortFields}
                                quickSearchSortable={quickSearchSortable}
                                quickSearchPreOptions={quickSearchPreOptions}
                                quickSearchOptions={quickSearchOptions}
                                quickSearchSelected={quickSearchSelected}
                                onSelectQuickSearch={setQuickSearchSelected}
                                onDeleteQuickSearch={deleteQuickSearchConfig}
                            >
                                <AddQuickSearch onClick={addQuickSearchConfig} />
                                {quickSearchOptions?.length ? (
                                    <SortQuickSearch onClick={sortQuickSearchConfig} />
                                ) : null}
                            </QuickSearchList>
                        )
                    }

                    return (
                        <div className={styles.bottomRow}>
                            <div className={styles.leftWrap}>{renderLeft()}</div>

                            <div>
                                <SearchArea />
                            </div>
                        </div>
                    )
                },
                visible: quickSearchEnable && quickSearchPlacement === QuickSearchPlacement.bottom
            }
        ].filter(v => v.visible)

        if (!list.length) {
            return null
        }

        return list.map(item => <React.Fragment key={item.key}>{item.renderComponent()}</React.Fragment>)
    }

    const topQuickSearchNode = renderTopQuickSearchNode()
    const bottomQuickSearchNode = renderBottomQuickSearchNode()

    const nodeList = [
        {
            key: '1',
            renderComponent() {
                return headerNode
            },
            visible: headerNode // 老的字段，不行出外部输入的是什么，暂不内部处理border
        },
        {
            key: '2',
            renderComponent() {
                return headerAfterLineNode
            },
            visible: headerAfterLineNode // 老的字段，不行出外部输入的是什么，暂不内部处理border
        },
        {
            key: '3',
            renderComponent() {
                return topQuickSearchNode
            },
            visible: topQuickSearchNode,
            border: {
                marginBottom: '8px'
            }
        },
        {
            key: '4',
            renderComponent() {
                return <CustomFieldsList fields={customFields} />
            },
            visible: !!customFields?.length,
            border: {
                marginBottom: '8px'
            }
        },
        {
            key: '5',
            renderComponent() {
                if (groupLayout) {
                    return <FieldItemGroup fieldsGroup={fieldsGroup} />
                }

                return (
                    <div className={styles.listWrap}>
                        <FieldItemList showLabel={showFieldLabel} showBtn={true} fieldsList={fieldsList} />
                    </div>
                )
            },
            visible: true,
            border: {
                marginBottom: '8px'
            }
        },
        {
            key: '6',
            renderComponent() {
                return bottomQuickSearchNode
            },
            visible: bottomQuickSearchNode
        }
    ].filter(v => v.visible)

    return (
        <ConfigProvider value={contextValue}>
            <div ref={widthRef} id={tableNameAndId} className={classnames(middleStyles.containerWrap, className)}>
                <Form
                    name={name}
                    form={form}
                    scrollToFirstError
                    onValuesChange={handleValuesChange}
                    initialValues={initialValues}
                >
                    <div className={classnames(styles.bg, contentClassName)}>
                        {nodeList.map((item, index) => {
                            const renderBorder = () => {
                                if (index === nodeList.length - 1) {
                                    return null
                                }

                                if (!item.border) {
                                    return null
                                }

                                if (typeof item.border === 'boolean') {
                                    return <div className={styles.line} />
                                }

                                return <div className={styles.line} style={item.border} />
                            }

                            return (
                                <React.Fragment key={item.key}>
                                    {item.renderComponent()}
                                    {renderBorder()}
                                </React.Fragment>
                            )
                        })}
                    </div>
                </Form>
            </div>
        </ConfigProvider>
    )
})
